// EmptyState — "there is nothing here" done once, properly.
//
// The old admin wrote a bare "No data" cell in a dozen places. An empty state
// is a real UI moment: it should say what would be here, and offer the action
// that creates the first one. Sections that genuinely have no create action
// (audit log, webhook events) pass no `action` and just get the explanation.

import { cn } from '@fajrarabi/ui';
import type { ReactNode } from 'react';

import { Icon, type IconName } from './Icon';

export interface EmptyStateProps {
  title: string;
  description?: string;
  /** The primary action that would create the first record. */
  action?: ReactNode;
  icon?: IconName;
  className?: string;
  /** `compact` fits inside a card or table; `page` centres in a full viewport. */
  size?: 'compact' | 'page';
}

export function EmptyState({
  title,
  description,
  action,
  icon = 'inbox',
  className,
  size = 'compact',
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'page' ? 'min-h-[60vh] px-6 py-16' : 'px-6 py-14',
        className,
      )}
    >
      <div className="grid h-12 w-12 place-items-center rounded-full bg-admin-100 text-admin-400">
        <Icon name={icon} className="h-6 w-6" />
      </div>
      <p className="mt-4 text-sm font-semibold text-admin-800">{title}</p>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-admin-500">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
